import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Mail, Phone, ExternalLink, Send } from 'lucide-react';
import { SiWhatsapp, SiInstagram } from 'react-icons/si';
import { restaurantInfo } from '@/data/index';
import { RESTAURANT_CONSTANTS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

const Contact = () => {
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(RESTAURANT_CONSTANTS.ADDRESS)}`;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-accent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Contactez-nous
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="text-lg opacity-90 max-w-2xl mx-auto"
          >
            Une question ? Une commande spéciale ? Nous sommes à votre écoute pour vous offrir le meilleur de la cuisine africaine et européenne.
          </motion.p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          >
            {/* Info Columns */}
            <div className="space-y-8">
              <motion.div variants={staggerItem}>
                <h2 className="text-3xl font-bold text-primary mb-6">Nos Coordonnées</h2>
                <p className="text-muted-foreground mb-8">
                  Situé au cœur du 13ème arrondissement de Paris, nous préparons vos plats préférés avec passion.
                </p>
              </motion.div>

              <div className="grid gap-6">
                <motion.div variants={staggerItem}>
                  <Card className="border-none shadow-sm bg-secondary/30">
                    <CardContent className="flex items-start gap-4 p-6">
                      <div className="p-3 bg-primary/10 rounded-xl text-primary">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Adresse</h3>
                        <p className="text-muted-foreground">{restaurantInfo.address}</p>
                        <a 
                          href={googleMapsUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-accent font-medium mt-2 hover:underline"
                        >
                          Voir sur Google Maps <ExternalLink className="w-4 h-4 ml-1" />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div variants={staggerItem}>
                  <Card className="border-none shadow-sm bg-secondary/30">
                    <CardContent className="flex items-start gap-4 p-6">
                      <div className="p-3 bg-primary/10 rounded-xl text-primary">
                        <Clock className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Horaires d'ouverture</h3>
                        <p className="text-muted-foreground">{restaurantInfo.hours}</p>
                        <p className="text-xs text-muted-foreground/70 mt-1 italic">Ouvert tous les jours</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div variants={staggerItem}>
                  <Card className="border-none shadow-sm bg-secondary/30">
                    <CardContent className="flex items-start gap-4 p-6">
                      <div className="p-3 bg-primary/10 rounded-xl text-primary">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1">Email</h3>
                        <p className="text-muted-foreground">{restaurantInfo.email}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </div>

            {/* Call to Action Column */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col justify-center items-center lg:items-start space-y-8"
            >
              <div className="w-full p-8 md:p-12 bg-white rounded-3xl shadow-xl border border-border flex flex-col items-center text-center lg:text-left lg:items-start">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Prêt à commander ?</h2>
                <p className="text-muted-foreground mb-8">
                  Pour une commande rapide et personnalisée, contactez-nous directement sur WhatsApp ou par email.
                </p>
                
                <div className="w-full space-y-4">
                  <Button 
                    asChild
                    size="lg"
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold py-7 rounded-2xl shadow-lg hover:scale-[1.02] transition-all"
                  >
                    <a href={restaurantInfo.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3">
                      <SiWhatsapp className="w-6 h-6" />
                      Commander sur WhatsApp
                    </a>
                  </Button>

                  <Button 
                    asChild
                    variant="outline"
                    size="lg"
                    className="w-full border-primary text-primary hover:bg-primary/5 py-7 rounded-2xl font-semibold transition-all"
                  >
                    <a href={`mailto:${restaurantInfo.email}`} className="flex items-center justify-center gap-3">
                      <Send className="w-5 h-5" />
                      Envoyer un email
                    </a>
                  </Button>

                  <div className="pt-6 border-t border-border mt-6 w-full">
                    <p className="text-sm text-center lg:text-left text-muted-foreground mb-4">Suivez-nous sur les réseaux</p>
                    <div className="flex justify-center lg:justify-start gap-4">
                      <a 
                        href={restaurantInfo.instagram} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-4 bg-secondary rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
                      >
                        <SiInstagram className="w-6 h-6" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extra Message */}
              <div className="text-center lg:text-left">
                <p className="text-sm text-muted-foreground">
                  * Service de commande en ligne uniquement. Pas de réservation de table pour le moment.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer Info Section */}
      <section className="bg-secondary/50 py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-primary font-semibold text-lg">
            {RESTAURANT_CONSTANTS.NAME} — Par {RESTAURANT_CONSTANTS.OWNER}
          </p>
          <p className="text-muted-foreground mt-2">© 2026 Tous droits réservés.</p>
        </div>
      </section>
    </div>
  );
};

export default Contact;